import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../utils/api';

export default function AdminDashboard() {
  const { socket } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depositAlert, setDepositAlert] = useState(null);

  useEffect(() => {
    api.get('/admin/dashboard').then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('new_deposit_alert', (alert) => {
      setDepositAlert(alert);
      setTimeout(() => setDepositAlert(null), 6000);
    });
    return () => socket.off('new_deposit_alert');
  }, [socket]);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  const chartData = [
    { name: 'Users',       value: data?.stats?.totalUsers || 0 },
    { name: 'Platforms',   value: data?.stats?.totalPlatforms || 0 },
    { name: 'Offers',      value: data?.stats?.totalOffers || 0 },
    { name: 'Memberships', value: data?.stats?.totalMemberships || 0 },
    { name: 'Claims',      value: data?.stats?.totalOfferClaims || 0 },
  ];

  return (
    <div>
      {/* Deposit alert toast */}
      {depositAlert && (
        <div style={{ position: 'fixed', top: 80, right: 24, background: 'var(--bg-card)', border: '2px solid #10b981', borderRadius: 12, padding: '14px 18px', zIndex: 9999, maxWidth: 300, boxShadow: '0 4px 20px rgba(16,185,129,0.3)', animation: 'fadeIn 0.3s ease' }}>
          <div style={{ fontSize: 13, color: '#10b981', fontWeight: 700, marginBottom: 4 }}>💰 New Deposit Request!</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{depositAlert.message}</div>
          <Link to="/admin/deposits" style={{ display: 'block', marginTop: 8, color: '#f5c400', fontSize: 13, fontWeight: 700 }}>Review Now →</Link>
        </div>
      )}

      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 8 }}>DASHBOARD</h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 32 }}>Welcome back! Here's an overview of your platform.</p>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Total Users',   value: data?.stats?.totalUsers,        icon: '👥', color: '#f5c400', link: '/admin/users'       },
          { label: 'Platforms',     value: data?.stats?.totalPlatforms,     icon: '🎮', color: '#c9a000', link: '/admin/platforms'   },
          { label: 'Active Offers', value: data?.stats?.totalOffers,        icon: '🎁', color: '#10b981', link: '/admin/offers'      },
          { label: 'Memberships',   value: data?.stats?.totalMemberships,   icon: '💎', color: '#7c3aed', link: '/admin/memberships' },
          { label: 'Offer Claims',  value: data?.stats?.totalOfferClaims,   icon: '✅', color: '#ef4444'                             },
        ].map(s => (
          <div key={s.label} className="card" style={{ borderLeft: `3px solid ${s.color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>{s.label}</span>
              <span style={{ fontSize: 20 }}>{s.icon}</span>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 900, color: s.color }}>{s.value ?? 0}</div>
            {s.link && <Link to={s.link} style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, display: 'block' }}>View all →</Link>}
          </div>
        ))}
      </div>

      {/* Chart + Recent */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 20, color: 'var(--text-secondary)' }}>OVERVIEW</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
              <Bar dataKey="value" fill="url(#barGrad)" radius={[4,4,0,0]} />
              <defs>
                <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f5c400" />
                  <stop offset="100%" stopColor="#c9a000" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 20, color: 'var(--text-secondary)' }}>NEW USERS</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data?.recentUsers?.map(u => (
              <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#0a0900', flexShrink: 0 }}>{u.username?.[0]?.toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.username}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(u.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
            {(!data?.recentUsers?.length) && <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>No users yet</div>}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 16, color: 'var(--text-secondary)' }}>QUICK ACTIONS</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: '+ Add Platform',   link: '/admin/platforms',   color: '#f5c400'  },
            { label: '+ Create Offer',   link: '/admin/offers',      color: '#10b981'  },
            { label: '+ Add Membership', link: '/admin/memberships', color: '#7c3aed'  },
            { label: '💰 View Deposits', link: '/admin/deposits',    color: '#10b981'  },
            { label: '⭐ Reviews',       link: '/admin/reviews',     color: '#f5c400'  },
            { label: '💳 Payments',      link: '/admin/payments',    color: '#c9a000'  },
            { label: '✏ Edit About',    link: '/admin/about',       color: '#ef4444'  },
          ].map(a => (
            <Link key={a.label} to={a.link} className="btn btn-secondary btn-sm" style={{ borderColor: a.color, color: a.color }}>{a.label}</Link>
          ))}
        </div>
      </div>
    </div>
  );
}
